import { type Component } from "vue";

import { getPropertyDisplayTitle } from "~/common/propertyDisplay";
import { UNASSIGNED_ASSIGNEE_LABEL, UNASSIGNED_PSEUDO_ASSIGNEE_KEY } from "~/components/visualization/constants";
import { NONNULL_PRIORITY_OPTIONS, PRIORITY_COLOR_MAP } from "~/constants/priority";
import {
  EXPONENTIAL_SIZES,
  FIBONACCI_SIZES,
  LINEAR_SIZES,
  NUMBER_SIZE_TO_PRETTY_LABEL,
  NUMBER_SIZE_TO_TSHIRT_SIZE,
  NUMBER_SIZES,
  SIZE_FORMAT_TO_COLOR_MAP,
} from "~/constants/size";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import {
  CheckedIcon,
  DueDateFieldIcon,
  MultiselectFieldIcon,
  PriorityIcon,
  SelectFieldIcon,
  SizeIcon,
  StartDateFieldIcon,
  UnassignedIcon,
  UncheckedIcon,
} from "~/icons";
import { isDefined } from "~/shared/common";
import { IconSize, PageKind, PropertyKind, SizeFormat, TypeaheadSpecialEntity } from "~/shared/enums";
import type { Dartboard, Folder, PropertyConfigTypeahead, TypeaheadOption, User } from "~/shared/types";
import type { usePageStore } from "~/stores";
import type { DataStore } from "~/stores/DataStore";
import { getPageDisplayName } from "~/utils/common";
import { getBestDateOptions } from "~/utils/date";
import { getRelativeTimeForDatesDate } from "~/utils/time";
import { getUserDisplayName } from "~/utils/user";

// Initialize
type PageStore = ReturnType<typeof usePageStore>;

let dataStore: DataStore;
let pageStore: PageStore;

let Avatar: Component;
let PageIcon: Component;
let StatusIcon: Component;

export const init = (newDataStore: DataStore, avatar: Component, pageIcon: Component, statusIcon: Component) => {
  dataStore = newDataStore;
  pageStore = dataStore.$usePageStore();

  Avatar = avatar;
  PageIcon = pageIcon;
  StatusIcon = statusIcon;
};

export const KIND_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_KIND> = (property) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: dataStore.taskKindList.map((kind) => ({
            value: kind.duid,
            label: kind.title,
            property,
            adtlSearchTerms: ["type", getPropertyDisplayTitle(property)],
            icon: PageIcon,
            iconArgs: { page: kind },
            chipColorHex: DEFAULT_CHIP_COLOR,
            chipIcon: PageIcon,
            chipIconArgs: { page: kind, size: IconSize.S },
          })),
          total: dataStore.taskKindList.length,
        }),
    ],
  },
];

export const DARTBOARD_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_DARTBOARD> = (property) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: dataStore.getDartboardList({ excludeFinished: true }).map((dartboard) => ({
            value: dartboard.duid,
            label: getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev"),
            property,
            adtlSearchTerms: [
              "dartboard",
              getPropertyDisplayTitle(property),
              getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "full"),
            ],
            icon: PageIcon,
            iconArgs: { page: dartboard },
            chipColorHex: DEFAULT_CHIP_COLOR,
            chipIcon: PageIcon,
            chipIconArgs: { page: dartboard, size: IconSize.S },
          })),
          total: dataStore.getDartboardList({ excludeFinished: true }).length,
        }),
    ],
  },
];

export const STATUS_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_STATUS | PropertyKind.STATUS> = (
  property
) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: dataStore.getStatusList(property).map((status) => ({
            value: status.duid,
            label: status.title,
            property,
            adtlSearchTerms: ["status", status.kind, getPropertyDisplayTitle(property)],
            icon: StatusIcon,
            iconArgs: { class: "icon-md", duid: status.duid, colorHex: status.colorHex },
            chipColorHex: status.colorHex,
            chipIcon: StatusIcon,
            chipIconArgs: { duid: status.duid, isContrast: true },
          })),
          total: dataStore.getStatusList(property).length,
        }),
    ],
  },
];

export const USER_TYPEAHEAD: PropertyConfigTypeahead<
  PropertyKind.DEFAULT_ASSIGNEES | PropertyKind.DEFAULT_REVIEWERS | PropertyKind.USER
> = (property) => {
  const appStore = dataStore.$useAppStore();
  const { currentPage } = appStore;
  const propSearchTerm =
    property.kind === PropertyKind.DEFAULT_ASSIGNEES
      ? "assignee"
      : property.kind === PropertyKind.DEFAULT_REVIEWERS
        ? "reviewer"
        : "user";
  const options: TypeaheadOption[] = [];

  let filteredUsers: User[];

  if (currentPage?.pageKind === PageKind.DARTBOARD || currentPage?.pageKind === PageKind.FOLDER) {
    filteredUsers = dataStore.getRelevantUsersByDartboardsOrFolders([currentPage] as Dartboard[] | Folder[]);
  } else {
    filteredUsers = dataStore.getUserList();
  }

  filteredUsers.forEach((user) => {
    options.push({
      value: user.duid,
      label: getUserDisplayName(user),
      specialEntity: TypeaheadSpecialEntity.USER,
      property,
      adtlSearchTerms: [propSearchTerm, user.email, getPropertyDisplayTitle(property)],
      icon: Avatar,
      iconArgs: {
        user,
        class: "icon-md",
      },
      chipColorHex: user.colorHex,
      chipIcon: Avatar,
      chipIconArgs: {
        user,
        isContrast: pageStore.theme === "light",
      },
    });
  });
  options.push({
    value: UNASSIGNED_PSEUDO_ASSIGNEE_KEY,
    label: UNASSIGNED_ASSIGNEE_LABEL,
    property,
    adtlSearchTerms: [propSearchTerm, "unassigned"],
    icon: UnassignedIcon,
    iconArgs: {
      class: "icon-md",
    },
    chipColorHex: DEFAULT_CHIP_COLOR,
    chipIcon: UnassignedIcon,
    chipIconArgs: {
      class: "icon-md",
    },
  });

  return [
    {
      trigger: getPropertyDisplayTitle(property).toLowerCase(),
      isMultiple: property.adtl.isMultiple,
      getOptions: [
        () =>
          Promise.resolve({
            key: property.duid,
            title: getPropertyDisplayTitle(property),
            options,
            total: options.length,
          }),
      ],
    },
  ];
};

export const DATES_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_DATES | PropertyKind.DATES> = (property) => {
  const makeOption = (isStart: boolean, dateStr: string, adtlSearchTerms: string[]): TypeaheadOption => ({
    value: dateStr,
    label: getRelativeTimeForDatesDate(dateStr),
    property,
    adtlSearchTerms: ["date", getPropertyDisplayTitle(property), ...adtlSearchTerms],
    icon: isStart ? StartDateFieldIcon : DueDateFieldIcon,
    iconArgs: {
      class: "icon-md",
    },
    chipIcon: isStart ? StartDateFieldIcon : DueDateFieldIcon,
    chipColorHex: DEFAULT_CHIP_COLOR,
  });

  // TODO reenable this and startDate below when UX is better
  if (property.kind !== PropertyKind.DEFAULT_DATES) {
    return [];
  }

  const propertyTitle = getPropertyDisplayTitle(property).toLowerCase();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startDate: ReturnType<PropertyConfigTypeahead<PropertyKind.DEFAULT_DATES | PropertyKind.DATES>>[number] = {
    trigger: `start ${propertyTitle}`,
    isMultiple: false,
    getOptions: [
      (query) =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: getBestDateOptions(query.toString()).map(({ dateStr, searchTerms }) =>
            makeOption(true, dateStr, ["start", ...searchTerms])
          ),
          total: getBestDateOptions(query.toString()).length,
        }),
    ],
  };

  const endDate: ReturnType<PropertyConfigTypeahead<PropertyKind.DEFAULT_DATES | PropertyKind.DATES>>[number] = {
    trigger: property.adtl.isRange
      ? `${property.kind === PropertyKind.DEFAULT_DATES ? "due" : "end"} ${propertyTitle}`
      : propertyTitle,
    isMultiple: false,
    getOptions: [
      (query) =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: getBestDateOptions(query.toString()).map(({ dateStr, searchTerms }) =>
            makeOption(false, dateStr, ["end", "due", ...searchTerms])
          ),
          total: getBestDateOptions(query.toString()).length,
        }),
    ],
  };

  return [endDate];
};

export const PRIORITY_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_PRIORITY> = (property) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: NONNULL_PRIORITY_OPTIONS.map((priority, i) => ({
            value: priority,
            label: priority.toString(),
            property,
            adtlSearchTerms: ["priority", getPropertyDisplayTitle(property), `p${i}`],
            icon: PriorityIcon,
            iconArgs: {
              class: "icon-md",
              style: { color: PRIORITY_COLOR_MAP.get(priority) },
            },
            chipColorHex: DEFAULT_CHIP_COLOR,
            chipIcon: PriorityIcon,
            chipIconArgs: {
              style: { color: PRIORITY_COLOR_MAP.get(priority) },
            },
          })),
          total: NONNULL_PRIORITY_OPTIONS.length,
        }),
    ],
  },
];

export const SIZE_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_SIZE> = (property) => {
  const colorMap = SIZE_FORMAT_TO_COLOR_MAP.get(property.adtl.format);

  const getSizeOptions = (sizes: number[]): TypeaheadOption[] =>
    sizes.map((size) => ({
      value: size,
      label: size.toString(),
      property,
      adtlSearchTerms: ["size", getPropertyDisplayTitle(property), size.toString()],
      icon: SizeIcon,
      iconArgs: {
        class: "icon-md",
        size,
        format: property.adtl.format,
        style: { color: colorMap?.get(size) },
      },
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: SizeIcon,
      chipIconArgs: {
        size,
        format: property.adtl.format,
        style: { color: colorMap?.get(size) },
      },
    }));

  return [
    {
      trigger: getPropertyDisplayTitle(property).toLowerCase(),
      isMultiple: false,
      getOptions: [
        (query) => {
          switch (property.adtl.format) {
            case SizeFormat.LINEAR: {
              return Promise.resolve({
                key: property.duid,
                title: getPropertyDisplayTitle(property),
                options: getSizeOptions(LINEAR_SIZES),
                total: LINEAR_SIZES.length,
              });
            }
            case SizeFormat.FIBONACCI: {
              return Promise.resolve({
                key: property.duid,
                title: getPropertyDisplayTitle(property),
                options: getSizeOptions(FIBONACCI_SIZES),
                total: FIBONACCI_SIZES.length,
              });
            }
            case SizeFormat.EXPONENTIAL: {
              return Promise.resolve({
                key: property.duid,
                title: getPropertyDisplayTitle(property),
                options: getSizeOptions(EXPONENTIAL_SIZES),
                total: EXPONENTIAL_SIZES.length,
              });
            }
            case SizeFormat.FREE_ENTRY: {
              const number = parseInt(query.toString(), 10);
              return Number.isNaN(number)
                ? Promise.resolve({
                    key: property.duid,
                    title: getPropertyDisplayTitle(property),
                    options: [],
                    total: 0,
                  })
                : Promise.resolve({
                    key: property.duid,
                    title: getPropertyDisplayTitle(property),
                    options: getSizeOptions([number]),
                    total: 1,
                  });
            }
            default: {
              return Promise.resolve({
                key: property.duid,
                title: getPropertyDisplayTitle(property),
                options: NUMBER_SIZES.filter(isDefined).map((size) => {
                  const label = NUMBER_SIZE_TO_TSHIRT_SIZE.get(size) ?? "";
                  const prettyLabel = NUMBER_SIZE_TO_PRETTY_LABEL.get(size) ?? "";

                  return {
                    value: size,
                    label,
                    property,
                    adtlSearchTerms: ["size", getPropertyDisplayTitle(property), size.toString(), prettyLabel],
                    icon: SizeIcon,
                    iconArgs: {
                      class: "icon-md",
                      size,
                      format: property.adtl.format,
                      style: { color: colorMap?.get(size) },
                    },
                    chipColorHex: DEFAULT_CHIP_COLOR,
                    chipIcon: SizeIcon,
                    chipIconArgs: {
                      size,
                      format: property.adtl.format,
                      style: { color: colorMap?.get(size) },
                    },
                  };
                }),
                total: NUMBER_SIZES.filter(isDefined).length,
              });
            }
          }
        },
      ],
    },
  ];
};

export const SELECT_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.SELECT> = (property) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: dataStore.getOptionList(property).map((option) => ({
            value: option.duid,
            label: option.title,
            property,
            adtlSearchTerms: ["option", getPropertyDisplayTitle(property)],
            icon: SelectFieldIcon,
            iconArgs: {
              class: "icon-md",
              style: { color: option.colorHex },
            },
            chipColorHex: option.colorHex,
          })),
          total: dataStore.getOptionList(property).length,
        }),
    ],
  },
];

export const MULTISELECT_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.DEFAULT_TAGS | PropertyKind.MULTISELECT> = (
  property
) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: true,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: dataStore.getOptionList(property).map((option) => ({
            value: option.duid,
            label: option.title,
            property,
            adtlSearchTerms: [
              property.kind === PropertyKind.DEFAULT_TAGS ? "tag" : "option",
              getPropertyDisplayTitle(property),
            ],
            icon: MultiselectFieldIcon,
            iconArgs: {
              class: "icon-md",
              style: { color: option.colorHex },
            },
            chipColorHex: option.colorHex,
          })),
          total: dataStore.getOptionList(property).length,
        }),
    ],
  },
];

export const CHECKBOX_TYPEAHEAD: PropertyConfigTypeahead<PropertyKind.CHECKBOX> = (property) => [
  {
    trigger: getPropertyDisplayTitle(property).toLowerCase(),
    isMultiple: false,
    getOptions: [
      () =>
        Promise.resolve({
          key: property.duid,
          title: getPropertyDisplayTitle(property),
          options: [
            {
              value: true,
              label: "Checked",
              property,
              adtlSearchTerms: [getPropertyDisplayTitle(property), "true", "t"],
              icon: CheckedIcon,
              iconArgs: { class: "icon-md" },
              chipColorHex: DEFAULT_CHIP_COLOR,
            },
            {
              value: false,
              label: "Unchecked",
              property,
              adtlSearchTerms: [getPropertyDisplayTitle(property), "false", "f"],
              icon: UncheckedIcon,
              iconArgs: { class: "icon-md" },
              chipColorHex: DEFAULT_CHIP_COLOR,
            },
          ],
          total: 2,
        }),
    ],
  },
];
